import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, DollarSign, Calculator, TrendingUp, MapPin, AlertTriangle } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    padding: 2rem 1.5rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 1.75rem 1.25rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    padding: 1.25rem 1rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    padding: 1.5rem 1rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    padding: 1.25rem 0.875rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    padding: 1rem 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.75rem 0.5rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: ${brandTheme.colors.primary[700]};
    gap: 0.75rem;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 3rem;

  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.2;
    margin-bottom: 1.5rem;
    color: ${brandTheme.colors.secondary[900]};

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      font-size: clamp(1.5rem, 6vw, 2.25rem);
      line-height: 1.1;
      margin-bottom: 1rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      font-size: clamp(1.25rem, 4vw, 1.75rem);
      margin-bottom: 0.75rem;
    }
  }

  .excerpt {
    font-size: 1.25rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
    margin-bottom: 2rem;

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      font-size: 1.1rem;
      line-height: 1.5;
      margin-bottom: 1.5rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      font-size: 1.1rem;
      margin-bottom: 1rem;
    }
  }

  .meta {
    display: flex;
    gap: 2rem;
    align-items: center;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
    flex-wrap: wrap;

    svg {
      width: 18px;
      height: 18px;
      margin-right: 0.5rem;
    }

    /* iPad Air and Medium Tablets */
    @media (max-width: 1024px) and (min-width: 769px) {
      gap: 1.5rem;
    }

    /* Landscape Mobile and Small Tablets */
    @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
      gap: 1rem;
      font-size: 0.9rem;
    }

    /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
    @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
      gap: 1.5rem;
    }

    /* iPhone 12 and Standard Mobile */
    @media (max-width: 427px) and (min-width: 390px) {
      gap: 1rem;
      font-size: 0.9rem;
    }

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      gap: 0.75rem;
      font-size: 0.85rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      gap: 0.5rem;
      font-size: 0.8rem;
    }
  }
`;

const ArticleImage = styled.div`
  height: 400px;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
  border-radius: ${brandTheme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 350px;
    margin-bottom: 2.5rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 350px;
    margin-bottom: 2.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 250px;
    margin-bottom: 2rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 300px;
    margin-bottom: 2.5rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 250px;
    margin-bottom: 2rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 200px;
    margin-bottom: 1.5rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 150px;
    margin-bottom: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(59,130,246,0.1)"/></svg>');
  }

  .placeholder {
    font-size: 4rem;
    color: ${brandTheme.colors.primary[300]};
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 1.05rem;
    line-height: 1.7;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  h2 {
    font-size: 1.75rem;
    margin: 3rem 0 1.5rem 0;
    color: ${brandTheme.colors.secondary[900]};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: -1.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 24px;
      background: linear-gradient(180deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[700]});
      border-radius: 2px;

      /* iPad Air and Medium Tablets */
      @media (max-width: 1024px) and (min-width: 769px) {
        left: -1.25rem;
      }

      /* iPhone SE and Small Mobile */
      @media (max-width: 389px) and (min-width: 320px) {
        left: -0.75rem;
        width: 3px;
        height: 20px;
      }

      /* Landscape orientation adjustments */
      @media (orientation: landscape) and (max-height: 500px) {
        left: -0.75rem;
        width: 3px;
        height: 18px;
      }
    }

    /* iPad Air and Medium Tablets */
    @media (max-width: 1024px) and (min-width: 769px) {
      font-size: 1.6rem;
      margin: 2.5rem 0 1.25rem 0;
      margin-left: 1.25rem;
    }

    /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
    @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
      font-size: 1.5rem;
      margin-left: 1rem;
    }

    /* iPhone 12 and Standard Mobile */
    @media (max-width: 427px) and (min-width: 390px) {
      font-size: 1.4rem;
      margin-left: 0.75rem;
    }

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      font-size: 1.3rem;
      margin: 2rem 0 1rem 0;
      margin-left: 0.75rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      font-size: 1.2rem;
      margin: 1.5rem 0 0.75rem 0;
      margin-left: 0.75rem;
    }
  }

  h3 {
    font-size: 1.4rem;
    margin: 2.5rem 0 1rem 0;
    color: ${brandTheme.colors.secondary[900]};

    /* iPad Air and Medium Tablets */
    @media (max-width: 1024px) and (min-width: 769px) {
      font-size: 1.3rem;
      margin: 2rem 0 0.875rem 0;
    }

    /* iPhone 12 and Standard Mobile */
    @media (max-width: 427px) and (min-width: 390px) {
      font-size: 1.25rem;
      margin: 2rem 0 0.875rem 0;
    }

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      font-size: 1.2rem;
      margin: 1.75rem 0 0.75rem 0;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      font-size: 1.15rem;
      margin: 1.5rem 0 0.5rem 0;
    }
  }

  p {
    margin-bottom: 1.5rem;

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      margin-bottom: 1.25rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      margin-bottom: 1rem;
    }
  }

  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;

    /* iPhone SE and Small Mobile */
    @media (max-width: 389px) and (min-width: 320px) {
      margin: 1.25rem 0;
      padding-left: 1.5rem;
    }

    /* Landscape orientation adjustments */
    @media (orientation: landscape) and (max-height: 500px) {
      margin: 1rem 0;
      padding-left: 1.25rem;
    }

    li {
      margin-bottom: 0.75rem;

      /* iPhone SE and Small Mobile */
      @media (max-width: 389px) and (min-width: 320px) {
        margin-bottom: 0.5rem;
      }

      /* Landscape orientation adjustments */
      @media (orientation: landscape) and (max-height: 500px) {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const CostTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  background: white;
  border-radius: ${brandTheme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid ${brandTheme.colors.neutral.gray[200]};
  }

  th {
    background: ${brandTheme.colors.primary[50]};
    font-weight: 600;
    color: ${brandTheme.colors.secondary[900]};
  }

  tr:last-child td {
    border-bottom: none;
  }

  .cost-highlight {
    font-weight: 600;
    color: ${brandTheme.colors.primary[600]};
  }
`;

const CostBreakdown = styled.div`
  background: ${brandTheme.colors.primary[50]};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 2rem;
  margin: 2rem 0;

  h3 {
    color: ${brandTheme.colors.primary[700]};
    margin-bottom: 1.5rem;
  }

  .cost-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid ${brandTheme.colors.primary[200]};

    &:last-child {
      border-bottom: none;
      font-weight: 600;
      font-size: 1.1rem;
      color: ${brandTheme.colors.primary[700]};
    }
  }
`;

const InfoBox = styled.div<{ type?: 'warning' | 'tip' }>`
  background: ${props => props.type === 'warning' ? '#fef3c7' : '#dbeafe'};
  border: 1px solid ${props => props.type === 'warning' ? '#fbbf24' : '#FECE1B'};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  svg {
    color: ${props => props.type === 'warning' ? '#f59e0b' : '#FECE1B'};
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  div {
    h4 {
      margin: 0 0 0.5rem 0;
      color: ${props => props.type === 'warning' ? '#92400e' : '#d97706'};
      font-size: 1rem;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: ${props => props.type === 'warning' ? '#92400e' : '#d97706'};
    }
  }
`;

const CalculatorBox = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  color: white;
  margin: 3rem 0;
  text-align: center;

  h3 {
    color: white;
    margin-bottom: 1rem;
  }

  .calculator-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin: 2rem 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    input, select {
      padding: 0.75rem;
      border: none;
      border-radius: ${brandTheme.borderRadius.lg};
      font-size: 1rem;
    }

    button {
      grid-column: 1 / -1;
      padding: 0.75rem;
      background: white;
      color: ${brandTheme.colors.primary[600]};
      border: none;
      border-radius: ${brandTheme.borderRadius.lg};
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: ${brandTheme.colors.primary[50]};
      }
    }
  }

  .result {
    background: rgba(255, 255, 255, 0.2);
    padding: 1rem;
    border-radius: ${brandTheme.borderRadius.lg};
    margin-top: 1rem;
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const ResinDrivewayCostGuidePage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Resin Driveway Cost Guide: What to Expect in 2025 | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Complete breakdown of resin driveway costs in 2025. Get accurate estimates including materials, labor, and factors affecting pricing. Free quotes available." />
        <meta name="keywords" content="resin driveway cost, resin bound driveway price, driveway installation cost, London driveway prices, resin surfacing cost 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/blog/resin-driveway-cost-guide/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Resin Driveway Cost Guide: What to Expect in 2025" />
        <meta property="og:description" content="Complete breakdown of resin driveway costs in 2025. Get accurate estimates including materials, labor, and factors affecting pricing." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/blog/resin-driveway-cost-guide/" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work3.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Resin driveway cost guide 2025" />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:published_time" content="2025-01-12T00:00:00+00:00" />
        <meta property="article:modified_time" content="2025-01-12T00:00:00+00:00" />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content="Cost Guide" />
        <meta property="article:tag" content="resin driveway cost" />
        <meta property="article:tag" content="resin bound driveway price" />
        <meta property="article:tag" content="driveway installation cost" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Resin Driveway Cost Guide: What to Expect in 2025" />
        <meta name="twitter:description" content="Complete breakdown of resin driveway costs in 2025. Get accurate estimates including materials, labor, and factors affecting pricing." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work3.jpeg" />
        <meta name="twitter:image:alt" content="Resin driveway cost guide 2025" />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="en-GB" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "Resin Driveway Cost Guide: What to Expect in 2025",
            "description": "Complete breakdown of resin driveway costs in 2025. Get accurate estimates including materials, labor, and factors affecting pricing.",
            "url": "https://www.resinsurfaceslondon.co.uk/blog/resin-driveway-cost-guide/",
            "datePublished": "2025-01-12T00:00:00+00:00",
            "dateModified": "2025-01-12T00:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "London",
                "addressCountry": "GB"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.resinsurfaceslondon.co.uk/blog/resin-driveway-cost-guide/"
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work3.jpeg",
              "width": 1200,
              "height": 630
            },
            "wordCount": 2800,
            "timeRequired": "PT8M",
            "articleSection": "Cost Guide",
            "keywords": "resin driveway cost, resin bound driveway price, driveway installation cost, London driveway prices, resin surfacing cost 2025",
            "inLanguage": "en-GB",
            "isAccessibleForFree": true,
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": "h1, h2, h3, .excerpt"
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Resin Driveway Costs"
              },
              {
                "@type": "Thing", 
                "name": "Driveway Installation"
              },
              {
                "@type": "Thing",
                "name": "London Construction"
              }
            ]
          })}
        </script>
      </Helmet>

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <ArticleHeader>
          <span className="category">Cost Guide</span>
          <h1>Resin Driveway Cost Guide: What to Expect in 2025</h1>
          <p className="excerpt">
            Complete breakdown of resin driveway costs, including materials, labor, and factors that affect pricing. Get accurate estimates for your project.
          </p>
          <div className="meta">
            <span>
              <Calendar size={18} />
              January 12, 2025
            </span>
            <span>
              <Clock size={18} />
              8 min read
            </span>
            <span>
              <DollarSign size={18} />
              Cost Guide
            </span>
          </div>
        </ArticleHeader>

        <ArticleImage>
          <img 
            src="/images/resin-surfaces-bound-work3.jpeg" 
            alt="Resin driveway cost guide showing professional installation and pricing"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit'
            }}
          />
        </ArticleImage>

        <ArticleContent>
          <p>
            Planning a new resin driveway but unsure about costs? You're not alone. Understanding resin driveway pricing helps you budget effectively and compare quotes accurately. This comprehensive guide breaks down everything you need to know about resin driveway costs in 2025.
          </p>

          <h2>Average Resin Driveway Costs in 2025</h2>

          <p>
            The cost of a resin bound driveway varies significantly based on several factors. Here's what you can expect to pay:
          </p>

          <CostTable>
            <thead>
              <tr>
                <th>Driveway Size</th>
                <th>Basic Installation</th>
                <th>Premium Installation</th>
                <th>Cost per m²</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Small (30-50m²)</td>
                <td>£1,500 - £2,500</td>
                <td>£2,500 - £4,000</td>
                <td className="cost-highlight">£50 - £80</td>
              </tr>
              <tr>
                <td>Medium (50-80m²)</td>
                <td>£2,500 - £4,000</td>
                <td>£4,000 - £6,400</td>
                <td className="cost-highlight">£50 - £80</td>
              </tr>
              <tr>
                <td>Large (80-120m²)</td>
                <td>£4,000 - £6,000</td>
                <td>£6,400 - £9,600</td>
                <td className="cost-highlight">£50 - £80</td>
              </tr>
              <tr>
                <td>Extra Large (120m²+)</td>
                <td>£6,000+</td>
                <td>£9,600+</td>
                <td className="cost-highlight">£45 - £75</td>
              </tr>
            </tbody>
          </CostTable>

          <InfoBox type="tip">
            <Calculator size={20} />
            <div>
              <h4>Cost Calculator</h4>
              <p>Use our quick calculator below to get an instant estimate for your project based on size and requirements.</p>
            </div>
          </InfoBox>

          <CalculatorBox>
            <h3>Quick Cost Calculator</h3>
            <p>Get an instant estimate for your resin driveway</p>
            <div className="calculator-form">
              <input type="number" placeholder="Driveway size (m²)" />
              <select>
                <option>Standard Installation</option>
                <option>Premium Installation</option>
                <option>Complex Site</option>
              </select>
              <button>Calculate Estimate</button>
            </div>
            <div className="result">
              Estimated Cost: £2,500 - £4,000
            </div>
          </CalculatorBox>

          <h2>Cost Breakdown: What's Included</h2>

          <p>
            Understanding what's included in your resin driveway quote helps you compare contractors fairly and avoid unexpected costs.
          </p>

          <CostBreakdown>
            <h3>Typical Cost Breakdown (80m² Driveway)</h3>
            <div className="cost-item">
              <span>Site preparation and excavation</span>
              <span>£400 - £800</span>
            </div>
            <div className="cost-item">
              <span>Sub-base material and installation</span>
              <span>£600 - £1,200</span>
            </div>
            <div className="cost-item">
              <span>Edging and restraints</span>
              <span>£300 - £600</span>
            </div>
            <div className="cost-item">
              <span>Resin bound materials</span>
              <span>£1,200 - £2,000</span>
            </div>
            <div className="cost-item">
              <span>Installation labor</span>
              <span>£800 - £1,600</span>
            </div>
            <div className="cost-item">
              <span>Drainage (if required)</span>
              <span>£200 - £800</span>
            </div>
            <div className="cost-item">
              <span>Total Project Cost</span>
              <span>£3,500 - £7,000</span>
            </div>
          </CostBreakdown>

          <h2>Factors Affecting Resin Driveway Costs</h2>

          <h3>1. Site Access and Preparation</h3>
          <ul>
            <li><strong>Easy Access:</strong> Standard costs apply</li>
            <li><strong>Difficult Access:</strong> +10-20% to labor costs</li>
            <li><strong>Existing Surface Removal:</strong> £5-15 per m²</li>
            <li><strong>Excavation Depth:</strong> Additional depth costs more</li>
          </ul>

          <h3>2. Driveway Size and Shape</h3>
          <ul>
            <li><strong>Larger Areas:</strong> Better value per m² (volume discounts)</li>
            <li><strong>Complex Shapes:</strong> +15-25% due to waste and complexity</li>
            <li><strong>Multiple Levels:</strong> Additional structural work required</li>
            <li><strong>Narrow Access:</strong> May require specialized equipment</li>
          </ul>

          <h3>3. Material Choices</h3>
          <ul>
            <li><strong>Standard Aggregates:</strong> Base price point</li>
            <li><strong>Premium Aggregates:</strong> +20-40% material costs</li>
            <li><strong>Specialty Finishes:</strong> +30-50% for unique effects</li>
            <li><strong>UV-Stable Resins:</strong> Slightly higher material costs</li>
          </ul>

          <h3>4. Location and Regional Variations</h3>
          <ul>
            <li><strong>London:</strong> 15-25% premium due to higher costs</li>
            <li><strong>South East:</strong> 10-15% above national average</li>
            <li><strong>Midlands:</strong> National average pricing</li>
            <li><strong>North:</strong> 5-10% below national average</li>
          </ul>

          <h3>5. Additional Requirements</h3>
          <ul>
            <li><strong>Planning Permission:</strong> £200-500 application fees</li>
            <li><strong>Building Regulations:</strong> May require structural calculations</li>
            <li><strong>Drainage Systems:</strong> £500-2,000 additional</li>
            <li><strong>Gates and Barriers:</strong> £300-1,500 each</li>
            <li><strong>Lighting Integration:</strong> £200-800</li>
          </ul>

          <InfoBox type="warning">
            <AlertTriangle size={20} />
            <div>
              <h4>Hidden Costs to Watch For</h4>
              <p>Always check if your quote includes VAT, waste disposal, and any necessary permits or planning applications.</p>
            </div>
          </InfoBox>

          <h2>Comparing Resin Bound vs Other Driveway Materials</h2>

          <CostTable>
            <thead>
              <tr>
                <th>Material</th>
                <th>Cost per m²</th>
                <th>Lifespan</th>
                <th>Maintenance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Resin Bound</td>
                <td className="cost-highlight">£50 - £80</td>
                <td>15-25 years</td>
                <td>Low</td>
              </tr>
              <tr>
                <td>Block Paving</td>
                <td>£40 - £70</td>
                <td>20-30 years</td>
                <td>Medium</td>
              </tr>
              <tr>
                <td>Concrete</td>
                <td>£30 - £50</td>
                <td>20-40 years</td>
                <td>Low</td>
              </tr>
              <tr>
                <td>Gravel</td>
                <td>£15 - £30</td>
                <td>5-10 years</td>
                <td>High</td>
              </tr>
              <tr>
                <td>Asphalt</td>
                <td>£25 - £40</td>
                <td>15-20 years</td>
                <td>Medium</td>
              </tr>
            </tbody>
          </CostTable>

          <h2>How to Get the Best Value</h2>

          <h3>1. Get Multiple Quotes</h3>
          <p>
            Always obtain at least 3 detailed quotes from reputable contractors. Compare not just price but also what's included, warranties, and timeline.
          </p>

          <h3>2. Timing Your Project</h3>
          <ul>
            <li><strong>Spring/Summer:</strong> Peak season, higher demand</li>
            <li><strong>Autumn:</strong> Good availability, favorable weather</li>
            <li><strong>Winter:</strong> Lower demand but weather risks</li>
            <li><strong>Off-Peak:</strong> Potential 5-10% savings</li>
          </ul>

          <h3>3. Consider Package Deals</h3>
          <p>
            Many contractors offer better rates for combined projects (driveway + pathway + garden landscaping).
          </p>

          <h3>4. Material Selection</h3>
          <ul>
            <li>Choose standard aggregates for cost-effectiveness</li>
            <li>Consider local materials to reduce transport costs</li>
            <li>Discuss bulk discounts for larger projects</li>
          </ul>

          <h2>Financing Options</h2>

          <h3>Payment Plans</h3>
          <ul>
            <li><strong>Deposit:</strong> Typically 25-50% upfront</li>
            <li><strong>Progress Payments:</strong> Based on completion milestones</li>
            <li><strong>Final Payment:</strong> Upon satisfactory completion</li>
          </ul>

          <h3>Financing Solutions</h3>
          <ul>
            <li>Home improvement loans</li>
            <li>Credit card payments (if 0% interest available)</li>
            <li>Contractor financing schemes</li>
            <li>Home equity loans</li>
          </ul>

          <h2>Value for Money Considerations</h2>

          <p>
            While resin bound driveways may cost more initially than some alternatives, consider the long-term value:
          </p>

          <ul>
            <li><strong>Durability:</strong> 15-25 year lifespan</li>
            <li><strong>Low Maintenance:</strong> Minimal ongoing costs</li>
            <li><strong>Property Value:</strong> Can increase home value by 5-10%</li>
            <li><strong>Permeability:</strong> May reduce drainage costs</li>
            <li><strong>Aesthetics:</strong> Premium appearance that lasts</li>
          </ul>

          <h2>Getting Your Quote</h2>

          <p>
            Ready to get a quote for your resin driveway? Here's what to expect:
          </p>

          <ol>
            <li><strong>Initial Consultation:</strong> Site visit and discussion of requirements</li>
            <li><strong>Detailed Survey:</strong> Measurements and site assessment</li>
            <li><strong>Written Quote:</strong> Detailed breakdown of all costs</li>
            <li><strong>Timeline:</strong> Project schedule and milestones</li>
            <li><strong>Warranty:</strong> Terms and conditions</li>
          </ol>

          <h2>Conclusion</h2>

          <p>
            Resin driveway costs in 2025 typically range from £45-£80 per square metre, with most residential projects costing between £2,500-£7,000. The exact cost depends on your specific requirements, site conditions, and location.
          </p>

          <p>
            While resin bound driveways represent a significant investment, they offer excellent long-term value through durability, low maintenance, and aesthetic appeal. By understanding the cost factors and getting multiple quotes, you can make an informed decision that fits your budget and requirements.
          </p>

          <p>
            Remember to choose a reputable contractor with proper insurance, warranties, and references. The cheapest quote isn't always the best value - consider quality, service, and long-term satisfaction when making your decision.
          </p>
        </ArticleContent>
      </ArticleContainer>
    </>
  );
};

export default ResinDrivewayCostGuidePage;
